/**
 * Fixture API responses for App Store screenshot mode. Served by the RTK
 * Query base query instead of the network when SCREENSHOT_MODE is on.
 * User "Jay", a Loss Day with a 1,813 kcal goal, breakfast + snack + lunch
 * logged, some water and exercise, and a few weeks of weigh-ins.
 */
import { addDays, format } from 'date-fns';
export const SCREENSHOT_USER_ID = 4242;
const KCAL_GOAL = 1813;
const LB = 0.45359237;
const dayStr = (offset) => format(addDays(new Date(), offset), 'yyyy-MM-dd');
const user = () => ({
    id: SCREENSHOT_USER_ID,
    signupDate: dayStr(-60),
    email: 'jay@example.com',
    firstName: 'Jay',
    lastName: 'K',
    role: { name: 'User' },
    programType: 'standard',
    weekStartsOn: 1,
    createdAt: dayStr(-60),
    updatedAt: dayStr(0),
    pendingDeletionRequest: null,
    programCompleted: false,
    profile: {
        birthDate: '1970-05-14',
        gender: 'M',
        phoneNumber: '',
        existingWeight: 205 * LB,
        idealWeight: 180 * LB,
        heightCm: 180,
        athleteMode: false,
        timezone: 'America/New_York',
    },
});
const done = (entryId, description, consumedTime, estimatedCalories) => ({ done: true, entryId, description, consumedTime, estimatedCalories });
const mealStatus = (date) => {
    const slots = {
        breakfast: done('ss-breakfast', 'Greek yogurt with blueberries and a drizzle of honey', '07:45', 310),
        morning_snack: done('ss-msnack', 'A crisp apple', '10:15', 95),
        lunch: done('ss-lunch', 'Grilled chicken salad with olive oil and lemon', '12:30', 480),
        afternoon_snack: { done: false },
        dinner: { done: false },
        evening_snack: { done: false },
    };
    return {
        userId: SCREENSHOT_USER_ID,
        date,
        slots,
        summary: {
            doneCount: 3,
            totalSlots: 6,
            doneSlots: ['breakfast', 'morning_snack', 'lunch'],
            requiredDoneCount: 2,
            requiredSlots: 3,
            snackDoneCount: 1,
            isComplete: false,
        },
        dayType: 'loss',
        defaultDayType: 'loss',
        isDayTypeOverridden: false,
        mealCaloriesTotal: 885,
        mealCaloriesPartial: false,
        mealsMissingEstimate: 0,
        restingBmr: KCAL_GOAL,
        restingBmrSource: 'scale',
        exerciseMinutes: 35,
        exerciseBurnKcal: 210,
        intakeVsRestingBmr: { delta: 885 - KCAL_GOAL, underOver: 'under' },
    };
};
/** Weight trend: ~205 lb six weeks ago down to ~198.4 lb today. */
const weighIns = () => Array.from({ length: 14 }, (_, i) => {
    const daysAgo = i * 3;
    const lb = 198.4 + daysAgo * 0.16 + (i % 2 ? 0.3 : 0);
    return {
        date: dayStr(-daysAgo),
        user: { id: SCREENSHOT_USER_ID },
        measurements: {
            weight: Math.round(lb * LB * 100) / 100,
            bodyFat: 24.1 + daysAgo * 0.03,
            ...(daysAgo === 0
                ? {
                    water: 6.3, // cups ≈ 3 bottles
                    exerciseMinutes: 35,
                    exerciseType: 'walking',
                    exerciseSessions: [{ exerciseType: 'walking', minutes: 35 }],
                }
                : {}),
        },
    };
});
const param = (url, key) => {
    const m = url.match(new RegExp(`[?&]${key}=([^&]+)`));
    return m ? decodeURIComponent(m[1]) : undefined;
};
/**
 * Returns an RTK Query result for a request in screenshot mode. Writes
 * (POST/PATCH/DELETE) succeed with an echo and change nothing.
 */
export const screenshotFixtureResponse = (args) => {
    const url = args.url.replace(/^\/+/, '');
    const method = (args.method || 'GET').toUpperCase();
    const path = url.split('?')[0].replace(/\/+$/, '');
    if (method !== 'GET') {
        return { data: args.body ?? {} };
    }
    if (path === 'auth/me' || path.startsWith('users/')) {
        return { data: user() };
    }
    if (path === 'mealEntries/status') {
        return { data: mealStatus(param(url, 'date') || dayStr(0)) };
    }
    if (path === 'biometrics') {
        const date = param(url, 'date');
        const all = weighIns();
        return { data: { data: date ? all.filter(b => b.date === date) : all } };
    }
    if (path === 'programState') {
        return {
            data: {
                state: 'standard',
                expectedDayType: 'loss',
                offPlanToday: false,
                lastOffPlanDate: null,
                recentOffPlanCount7: 0,
                suggestion: '',
            },
        };
    }
    if (path.startsWith('favorites')) {
        return { data: [] };
    }
    // Lists default to empty; everything else to an empty object.
    return { data: { data: [], hasNextPage: false } };
};
